import React, { useState } from "react";
import { Bell } from "lucide-react";
import { toast } from "sonner";

import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/shared/components/ui/dropdown-menu";
import PixelAvatar from "@/shared/components/PixelAvatar";
import {
  useMarkAllNoticesRead, useMarkNoticeRead, useNotices, useNotificationTypes, useUnreadCount, type Notice,
} from "@/shared/api/notifications";
import { useSettings } from "@/shared/api/settings";
import { ago } from "@/shared/lib/time";
import { invBtn } from "./TopBar";
import { pathOfHash, placeOf } from "../places";

import type { Agent } from "@/shared/types";

const MAX_BADGE = 99;
/* Why a task's slot started no run: the short form of the email's reason (templates.py). */
const NOT_FIRED: Record<string, string> = {
  agent_gone: "Its agent was deleted",
  not_published: "Its agent isn't published",
  out_of_credits: "Your workspace is out of credits",
  failed: "It couldn't start",
};

/** The notice's line and the smaller one under it, from its payload. */
function wording(notice: Notice, label: string | undefined): [string, string] {
  const p = notice.payload as Record<string, string>;
  const agent = p.agent_name ?? "An agent";
  switch (notice.type) {
    case "hitl.waiting":
      return [p.waiting_on === "question" ? `${agent} has a question for you` : `${agent} needs your approval`, p.title ?? ""];
    case "task.finished":
      return [`${p.task_name} task finished`, agent];
    case "task.failed":
      return [`${p.task_name} task failed`, agent];
    case "chat.finished":
      return [p.status === "error" ? `${agent} couldn't finish its reply` : `Your reply from ${agent} is ready`, ""];
    case "schedule.not_fired":
      return [`${p.task_name} task didn't run`, NOT_FIRED[p.reason] ?? agent];
    case "credits.low":
      return ["Your workspace is running low on credits", "Add credits or change your plan"];
    case "credits.exhausted":
      return ["Your workspace is out of credits", "A run was refused"];
    default: // a kind newer than this app: its label from the server, so the bell never breaks
      return [label ?? notice.type, p.agent_name ?? ""];
  }
}

function NoticeRow({ notice, agent, label, onOpen }: {
  notice: Notice;
  agent: Agent | undefined;
  label: string | undefined;
  onOpen: (notice: Notice) => void;
}) {
  const [line, detail] = wording(notice, label);
  const unread = !notice.read_at;
  return (
    <DropdownMenuItem
      data-notice
      onSelect={() => onOpen(notice)}
      className="press items-start gap-2.5 px-2 py-2 rounded-[8px] cursor-pointer hover:bg-[var(--accent-hover)] focus:bg-[var(--accent-hover)]"
    >
      {agent ? (
        <PixelAvatar seed={agent.seed} hue={agent.hue} size={22} ring rounded={6} />
      ) : (
        <span className="shrink-0 w-[22px] h-[22px] rounded-[6px] bg-[var(--surface-2)] flex items-center justify-center">
          <Bell className="w-3 h-3 text-[var(--text-tertiary)]" strokeWidth={1.75} />
        </span>
      )}
      <span className="min-w-0 flex-1">
        <span className={`block text-[13px] leading-[18px] ${unread ? "text-[var(--text-primary)] font-medium" : "text-[var(--text-tertiary)]"}`}>
          {line}
        </span>
        {detail && <span className="block text-[12px] text-[var(--text-tertiary)] truncate" title={detail}>{detail}</span>}
        <span className="block text-[11.5px] text-[var(--text-quaternary)] tabular-nums">{ago(notice.created_at)}</span>
      </span>
      {unread && (
        <>
          <span aria-hidden="true" className="shrink-0 mt-1.5 w-1.5 h-1.5 rounded-full bg-[var(--info)]" />
          <span className="sr-only">Unread</span>
        </>
      )}
    </DropdownMenuItem>
  );
}

/* The bell: your notices in this workspace (`/notifications`), newest first. The badge
   refetches on load and on `notification.created`; a missed event shows on the next load. */
export function NotificationsBell({ agents, onOpenLink }: {
  agents: Agent[];
  /** Goes to the page a notice names, by its path. */
  onOpenLink: (path: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const orgId = useSettings().data?.org?.id;
  const { data: unread = 0 } = useUnreadCount();
  const notices = useNotices(open);
  const { data: types } = useNotificationTypes();
  const markRead = useMarkNoticeRead();
  const markAllRead = useMarkAllNoticesRead();
  const rows = notices.data?.pages.flat() ?? [];

  // A notice's link is `/?org=<org>#/<view>/<id>/…` (backend/modules/notifications): the old
  // hash form, read by the same translation as any old link (`app/places.ts`).
  const openNotice = (notice: Notice) => {
    const url = new URL(notice.link, window.location.origin);
    const org = url.searchParams.get("org");
    if (org && org !== orgId) {
      // the app has no workspace switch yet: a sign-in acts for one workspace
      toast.info("This notice is from another workspace.");
      return;
    }
    if (!notice.read_at) markRead.mutate(notice.id);
    const path = pathOfHash(url.hash);
    if (!path) return;
    // an agent's page for one that is gone would quietly show Overview: say why instead. A chat
    // is left to open: Definable's chats name an agent that is in no list.
    const { view, agentId } = placeOf(path);
    if (view !== "chat" && agentId && !agents.some((a) => a.id === agentId)) toast.info("That agent was deleted.");
    else onOpenLink(path);
  };

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <button
          className={`${invBtn} relative`}
          title="Notifications"
          aria-label={unread > 0 ? `Notifications, ${unread} unread` : "Notifications"}
        >
          <Bell className="w-[14px] h-[14px]" strokeWidth={1.75} />
          {unread > 0 && (
            <span className="absolute -top-0.5 -right-0.5 min-w-[14px] h-[14px] px-[3px] rounded-full bg-[var(--warn)] text-white text-[8.5px] font-semibold flex items-center justify-center tabular-nums ring-2 ring-[var(--canvas)]">
              {unread > MAX_BADGE ? `${MAX_BADGE}+` : unread}
            </span>
          )}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-[340px] max-w-[92vw] p-1 rounded-[12px] bg-[var(--surface)] border border-[var(--hairline-strong)] shadow-menu">
        <div className="flex items-center justify-between gap-2 pl-2 pr-1 py-1">
          <DropdownMenuLabel className="section-label p-0">Notifications</DropdownMenuLabel>
          <DropdownMenuItem
            onSelect={(e) => {
              e.preventDefault(); // keep the menu open on the now-read list
              markAllRead.mutate();
            }}
            disabled={unread === 0 || markAllRead.isPending}
            className="press h-6 px-2 rounded-[8px] text-[12px] text-[var(--text-secondary)] whitespace-nowrap hover:bg-[var(--accent-hover)] hover:text-[var(--text-primary)] focus:bg-[var(--accent-hover)]"
          >
            Mark all read
          </DropdownMenuItem>
        </div>
        <DropdownMenuSeparator className="my-0.5" />
        <div className="max-h-[min(420px,60vh)] overflow-y-auto overflow-x-hidden">
          {notices.isPending ? (
            <div className="px-3 py-5 text-center text-[12px] shimmer-text">Loading notifications</div>
          ) : notices.isError ? (
            <div className="px-3 pt-5 pb-3 flex flex-col items-center gap-2 text-[12px] text-[var(--text-tertiary)]">
              Couldn't load notifications.
              <DropdownMenuItem
                onSelect={(e) => {
                  e.preventDefault(); // stay open to show the retried list
                  notices.refetch();
                }}
                className="press h-6 px-2 rounded-[8px] text-[12px] text-[var(--text-secondary)] hover:bg-[var(--accent-hover)] hover:text-[var(--text-primary)] focus:bg-[var(--accent-hover)]"
              >
                Try again
              </DropdownMenuItem>
            </div>
          ) : rows.length === 0 ? (
            <div className="px-3 py-5 text-center text-[12px] text-[var(--text-tertiary)]">
              Nothing yet. Agents waiting on you and finished tasks show up here.
            </div>
          ) : (
            <>
              {rows.map((notice) => (
                <NoticeRow
                  key={notice.id}
                  notice={notice}
                  agent={agents.find((a) => a.id === notice.agent_id)}
                  label={types?.find((t) => t.id === notice.type)?.label}
                  onOpen={openNotice}
                />
              ))}
              {notices.hasNextPage && (
                <DropdownMenuItem
                  onSelect={(e) => {
                    e.preventDefault(); // keep the menu open for the next page
                    notices.fetchNextPage();
                  }}
                  disabled={notices.isFetchingNextPage}
                  className="press justify-center px-2 py-1.5 rounded-[8px] text-[12.5px] text-[var(--text-secondary)] hover:bg-[var(--accent-hover)] focus:bg-[var(--accent-hover)]"
                >
                  {notices.isFetchingNextPage ? "Loading…" : "Load more"}
                </DropdownMenuItem>
              )}
            </>
          )}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
